import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, Output } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { UploadFile, UploadError } from '@shared/model';
import { DateTimeModalComponent } from '../date-time-modal/date-time-modal.component';
import { isFileNameValid, ProductNames } from '@mynexus/mynexus-ui-lib';
import { ProductService } from '@core/service/product.service';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: 'documents-upload',
    templateUrl: './documents-upload.component.html',
    styleUrls: ['./documents-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DialogService],
    standalone: false
})
export class DocumentsUploadComponent {
  @Input() isExternal!: boolean;
  @Input() authorizationNumber!: string | undefined;
  @Input() authorizationId!: string | undefined;
  @Input() autoUpload!: boolean;
  @Input() tenantId!: string;
  @Input() tenantName!: string;
  @Input() dateTimeFaxReceivedUTC: string | null = null;
  @Input() reviewConfirmationPopupNeeded = false;
  @Input() isHhAuthForPacUser = false;
  @Input() set documents(value: UploadFile[]) {
    this.uploadedFiles = value;
  }
  get documents() {
    return this.uploadedFiles;
  }

  @Output() uploadCompleted = new EventEmitter<void>();
  @Output() uploadedNewFile = new EventEmitter<UploadFile[]>();
  @Output() selectedFiles = new EventEmitter<UploadFile[]>();

  subsequentRequestType = 'Subsequent';
  orderDateTypeLabel = 'Signed';
  selectedDocumentTypes: string[] = [];
  fileValid = false;
  uploadedFiles: UploadFile[] = [];
  display = false;
  fileDescription!: string;
  IsNotReadyToUpload = false;
  showButton = false;
  isUploading = false;
  limitFour = false;
  fileTypeErrors = false;
  fileSizeErrors = false;
  fileNameSizeErrors = false;
  fileNameInvalidErrors = false;
  filesSizeSum = 0;
  productId!: string;

  hhAuthForPacDocumentTypes = [
  { label: 'Face sheet (Demo info)', value: 'Face sheet (Demo info)' },
  { label: 'Physician order', value: 'Physician order' },
  { label: 'Clinical Documentation', value: 'Clinical Documentation' },
  { label: 'Other', value: 'Other' }
];

  // need in database
  documentTypes = [
    { label: 'Initial Request', value: 'Initial Request' },
    { label: 'MD Order', value: 'MD Order' },
    { label: 'Plan of Care/485', value: 'Plan of Care/485' },
    { label: 'Evaluation', value: 'Evaluation' },
    { label: 'OASIS', value: 'OASIS' },
    { label: 'Reauth Request', value: 'Reauth Request' },
    { label: 'Patient Summary Report', value: 'Patient Summary Report' },
    { label: 'Wound Care Connect Image / Documentation', value: 'Wound Care Connect Image / Documentation' },
    { label: 'Visit Notes', value: 'Visit Notes' },
    { label: 'Resumption of Care OASIS', value: 'Resumption of Care OASIS' },
    { label: 'Recert OASIS', value: 'Recert OASIS' },
    { label: 'D/C OASIS', value: 'D/C OASIS' },
    { label: 'Retro', value: 'Retro' },
    { label: 'Additional Information', value: 'Additional Information' },
    { label: 'Duplicate', value: 'Duplicate' },
    { label: 'Miscellaneous', value: 'Miscellaneous' },
    { label: 'History and Physical', value: 'History and Physical' },
    { label: 'Discharge Summary', value: 'Discharge Summary' },
    { label: 'POA/AOR', value: 'POA/AOR' },
    { label: 'P2P', value: 'P2P' },
    { label: 'NOMNC', value: 'NOMNC' },
    { label: 'Aspire', value: 'Aspire' },
    { label: 'Intent to Deny', value: 'Intent to Deny' },
    { label: 'PSCCR', value: 'PSCCR' },
    { label: 'DENC', value: 'DENC' },
    { label: 'Provider Appeal', value: 'Provider Appeal' },
    { label: 'Member Appeal', value: 'Member Appeal' },
    { label: 'Denial Notification', value: 'Denial Notification' }
  ];

  allowedExtensions: string[] = ['.jpg', '.jpeg', '.png', '.pdf'];
  maxFileSize = 20 * 1024 * 1024; // 20Mb
  maxFileNameSize = 130;

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly dialogService: DialogService,
    private readonly confirmationService: ConfirmationService,
    private readonly productService: ProductService,
    private readonly destroyRef: DestroyRef,
  ) {
  }

  ngOnInit() {
    this.productService
      .getProductByShortName(ProductNames.HomeHealth)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(product => {
        this.productId = product.productId;
      });
  }

  showUploadButton(documentTypes: string[]) {
    this.showButton = documentTypes.length > 0;
  }

  sumAllFileSize() {
    this.filesSizeSum = this.uploadedFiles.reduce((sum, { fileMetaData }) => sum + (fileMetaData as any).size, 0);
  }

  onError(errors: UploadError[]) {
    this.fileTypeErrors = errors.some(e => e.type === 'accept');
    this.fileSizeErrors = errors.some(e => e.type === 'fileSize');
    this.fileNameSizeErrors = errors.some(e => e.type === 'fileNameSize');
    this.fileNameInvalidErrors = errors.some((e) => e.type === 'fileName');
    this.showDialog();
    this.hideButton();
    this.resetUploadFields();
  }

  resetValidationErrors() {
    this.fileTypeErrors = false;
    this.fileSizeErrors = false;
    this.fileNameSizeErrors = false;
    this.fileNameInvalidErrors = false;
  }

  // make sure no more than 4 files selected
  checkLimit() {
    this.limitFour = this.uploadedFiles.length >= 4;
  }

  addFile(files: File[]) {
    switch (true) {
      case this.isExternal:
        this.addSelected(files);
        break;
      case Boolean(this.dateTimeFaxReceivedUTC):
        this.addSelected(files, this.dateTimeFaxReceivedUTC as string);
        break;
      default:
        // eslint-disable-next-line no-case-declarations
        const ref = this.dialogService.open(DateTimeModalComponent, {
          header: 'Select the Date and Time Request Received',
          width: '500px',
          modal: true,
          closable: true,
        });

        ref.onClose.subscribe((dateTimeFaxReceived: string) => {
          if (dateTimeFaxReceived) {
            this.addSelected(files, dateTimeFaxReceived);
          } else {
            this.hideButton();
            this.resetUploadFields();
          }
          this.ref.markForCheck();
        });
    }
    this.ref.markForCheck();
  }

  confirmNeedCheckStaff(): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.confirmationService.confirm({
        accept: () => resolve(true),
        reject: () => resolve(false)
      });
    });
  }

  addFilenameTimestamp(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    const name = fileName.substr(0, lastDotIndex) || fileName;
    const ext = lastDotIndex > -1 ? fileName.substr(lastDotIndex) : '';

    const date = new Date();
    const addLeadingZero = (num: number): string => {
      return String(num).padStart(2, '0');
    }

    const timestamp = `${date.getFullYear()}${addLeadingZero(date.getMonth() + 1)}${addLeadingZero(date.getDate())}`
      + `${addLeadingZero(date.getHours())}${addLeadingZero(date.getMinutes())}${addLeadingZero(date.getSeconds())}`;

    return `${name}_${timestamp}${ext}`;
  }

  // add selected to temp list
  async addSelected(files: FileList | File[], dateTimeFaxReceived?: string) {
    const file: File = files[0];
    const indexNumber = this.uploadedFiles.length < 1 ? 0 : this.uploadedFiles.length;

    if (this.maxFileSize && this.filesSizeSum + file.size > this.maxFileSize) {
      const error: UploadError[] = [{ type: 'fileSize', name: file.name }];
      this.onError(error);
    } else if (!isFileNameValid(file.name).valid) {
          const error: UploadError[] = [{type: 'fileName', name: file.name}];
          this.onError(error);
    } else {
      this.resetValidationErrors();

      try {
        const fileBase64: string = await this.readFileAsync(file) as string;
        const fileContent = fileBase64.split(',')[1];

        this.uploadedFiles[indexNumber] = {
          fileMetaData: {
            size: file.size,
            type: file.type,
          },
          description: this.fileDescription,
          authorizationId: this.authorizationId,
          authorizationNumber: this.authorizationNumber,
          tenantId: this.tenantId,
          tenantName: this.tenantName,
          documentSource: 'Upload',
          //createdBy: this.authenticationService.getEmail as string,
          fileContent,
          documentTypes: this.selectedDocumentTypes,
          dateTimeFaxReceived: dateTimeFaxReceived || new Date().toUTCString(),
          documentFileName: this.addFilenameTimestamp(file.name),
          productId: this.productId,
          isAutomaticOCRDocument: false
        };

        this.uploadedFiles[indexNumber].isAutomaticOCRDocument=this.isDocTypeOASIS(indexNumber);
        if (this.reviewConfirmationPopupNeeded && this.isExternal) {
          this.uploadedFiles[indexNumber].isNeedCheckStaff = await this.confirmNeedCheckStaff();
        }

        this.hideButton();
        this.resetUploadFields();

        this.checkLimit();
        this.sumAllFileSize();

        if (this.autoUpload) {
          this.uploadedNewFile.emit(this.uploadedFiles);
        }
      } catch (error) {
        console.log(error);
      }
    }
    this.ref.markForCheck();
  }

  removeItem(index: number) {
    this.uploadedFiles.splice(index, 1);
    this.resetUploadFields();
    this.checkLimit();
    this.sumAllFileSize();
    this.uploadedNewFile.emit(this.uploadedFiles);
  }

  showDialog() {
    this.display = true;
  }

  // Upload new document
  uploadDocuments() {
    this.IsNotReadyToUpload = true;
    if (this.uploadedFiles.length > 0) {
      this.isUploading = true;
      const uploadFileDTOs: UploadFile[] = this.uploadedFiles.map(f => {
        const { fileMetaData, ...newFile } = f;
        return newFile;
      });
      this.selectedFiles.emit(uploadFileDTOs);
      this.filesSizeSum = 0;
      this.uploadedFiles = [];
      this.limitFour = false;
      this.hideButton();
      this.isUploading = false;
    }
  }

  private readFileAsync(file: File): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader: any = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private resetUploadFields() {
    this.fileDescription = '';
    this.selectedDocumentTypes = [];
  }

  private hideButton(): void {
    this.showButton = false;
  }

  private isDocTypeOASIS(index: number) {
    const file: UploadFile = this.uploadedFiles[index];
    const keyword = "OASIS";
    let isOasis = false;
    // checking whether file name contains OASIS or not
      isOasis = file?.documentFileName?.toUpperCase().includes(keyword) ?? false;
      if (!isOasis) {
        // checking whether description contains OASis or not
        if (file.description && file.description.trim() !== '' && file.description.toUpperCase().includes(keyword)) {
          isOasis = true;
        }
        else {
          // checking whether any of selected document types contains OASIS or not
          isOasis = file.documentTypes.some(b => b.toUpperCase().includes(keyword));
        }
      }
    return isOasis;
  }

}
